import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Tabs } from 'antd';
import { AccountBookOutlined, DollarOutlined, FileTextOutlined, IdcardOutlined } from '@ant-design/icons';
import { BackButton } from '@/components/back-button';
import { ContentBody, ContentHeader, ContentWrapper } from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import LedgerCard from '../_components/LedgerCard';
import { errorText, str } from '../_components/format';
import SupplierData from './_components/SupplierData';
import SupplierInvoices from './_components/SupplierInvoices';
import SupplierPayments from './_components/SupplierPayments';

const DEFAULT_PARAMS = Object.freeze({ offset: '1', limit: '50' });

/**
 * One supplier of a company, as a page of its own under Suppliers — the
 * customer view's pattern (back button, header with the name, content below),
 * with tabs: the card from the books (only under the ledger switch), the
 * supplier's invoices, the payments the bank statements show, and the data
 * (from the invoices, from the books, and the platform-only contact block).
 * Company and tab live in the address.
 */
function SupplierProfileContent() {
  const { t } = useTranslation(['suppliers', 'menus']);
  const { pib } = useParams();
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const company = str(queryParams.company);

  const { data: supplier, isLoading, error } = useQuery({
    queryKey: ['suppliers', company, 'profile', pib],
    queryFn: () => API.suppliers.getSupplier(company, pib),
    enabled: Boolean(company && pib),
    retry: false,
  });

  const showCard = Boolean(supplier?.ledger && supplier.books.length);
  const tabs = [
    showCard && {
      key: 'card',
      label: t('profile.tabs.card'),
      icon: <AccountBookOutlined />,
      children: (
        <LedgerCard
          company={company}
          view="supplier"
          partners={supplier.books.map((b) => ({ code: b.code, name: b.name }))}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
        />
      ),
    },
    {
      key: 'invoices',
      label: t('profile.tabs.invoices'),
      icon: <FileTextOutlined />,
      children: <SupplierInvoices company={company} pib={pib} queryParams={queryParams} setQueryParams={setQueryParams} />,
    },
    {
      key: 'payments',
      label: t('profile.tabs.payments'),
      icon: <DollarOutlined />,
      children: <SupplierPayments company={company} pib={pib} queryParams={queryParams} setQueryParams={setQueryParams} />,
    },
    {
      key: 'data',
      label: t('profile.tabs.data'),
      icon: <IdcardOutlined />,
      children: supplier ? <SupplierData company={company} supplier={supplier} /> : null,
    },
  ].filter(Boolean);
  const active = tabs.some((x) => x.key === queryParams.tab) ? queryParams.tab : tabs[0].key;

  return (
    <RootLayout metadata={{ title: supplier?.name || t('suppliers', { ns: 'menus' }) }}>
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <BackButton href={ROUTES.suppliers} />
          <ContentWrapper>
            <ContentHeader
              label={t('profile.label')}
              title={supplier ? `${supplier.name || supplier.pib}` : ''}
              isLoading={isLoading}
            >
              {supplier && (
                <span className="text-[13px] font-semibold text-white">
                  {t('columns.pib')} {supplier.pib}
                  {supplier.registrationNo ? ` · ${t('columns.registrationNo')} ${supplier.registrationNo}` : ''}
                </span>
              )}
            </ContentHeader>
            <ContentBody>
              <div className="with-y-overflow">
                <div className="grid gap-4 p-4">
                  {!company && <Alert type="info" showIcon message={t('pickCompany')} />}
                  {error && <Alert type="error" showIcon message={errorText(t, error)} />}
                  {supplier && (
                    <Tabs
                      activeKey={active}
                      onChange={(v) => setQueryParams({ tab: v, offset: 1, uOffset: 1, iOffset: 1, pOffset: 1 })}
                      items={tabs}
                    />
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

function SupplierProfileWithParams() {
  return (
    <QueryParamsProvider defaultParams={DEFAULT_PARAMS}>
      <SupplierProfileContent />
    </QueryParamsProvider>
  );
}

const SupplierProfilePage = withPrivateRoute(SupplierProfileWithParams, ROLES_PERMISSIONS.SUPPLIERS.VIEW);
SupplierProfilePage.displayName = 'SupplierProfilePage';

export default SupplierProfilePage;
